// StudentPortalRequest.js - Public page for students to request portal access
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Alert,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import PageLayout from 'examples/LayoutContainers/PageLayout';
import api from '../../../API/axiosInstance';
import { API_ENDPOINTS, API_URL } from 'config/api';

const StudentPortalRequest = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    erpStudentId: searchParams.get('erpId') || '',
    studentName: '',
    email: '',
    mobile: '',
    remarks: '',
  });

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [requestStatus, setRequestStatus] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.erpStudentId || !formData.studentName) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields (ERP Student ID and Name).',
        severity: 'error',
      });
      return;
    }

    setLoading(true);
    try {
      const endpoint = API_ENDPOINTS.STUDENT_REQUEST.CREATE_REQUEST.replace(API_URL, '');
      const response = await api.post(endpoint, formData);
      
      if (response.data) {
        setSubmitted(true);
        setRequestStatus(response.data);
        setSnackbar({
          open: true,
          message: 'Request submitted successfully! You will be notified once it is reviewed.',
          severity: 'success',
        });
      }
    } catch (error) {
      console.error('Error submitting request:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to submit request. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const checkStatus = async () => {
    if (!formData.erpStudentId) return;

    setLoading(true);
    try {
      const endpoint = API_ENDPOINTS.STUDENT_REQUEST.REQUEST_STATUS(formData.erpStudentId).replace(API_URL, '');
      const response = await api.get(endpoint);
      
      if (response.data) {
        setRequestStatus(response.data);
        setSubmitted(true);
      } else {
        setSnackbar({
          open: true,
          message: 'No request found for this ERP Student ID.',
          severity: 'info',
        });
      }
    } catch (error) {
      console.error('Error checking status:', error);
      setSnackbar({
        open: true,
        message: 'Failed to check request status.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted && requestStatus) {
    const statusColor = requestStatus.status === 'Approved' ? 'success' : 
                        requestStatus.status === 'Rejected' ? 'error' : 'warning';
    
    return (
      <PageLayout>
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          sx={{ bgcolor: '#f8f9fa' }}
        >
          <Card sx={{ maxWidth: 600, width: '100%', p: 3 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckCircleIcon 
                sx={{ fontSize: 64, mb: 2, color: statusColor + '.main' }} 
              />
              <MDTypography variant="h4" gutterBottom>
                Request Status
              </MDTypography>
              <Alert severity={statusColor} sx={{ mb: 2 }}>
                Status: <strong>{requestStatus.status}</strong>
              </Alert>
              
              {requestStatus.status === 'Approved' && (
                <MDTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Congratulations! Your request has been approved. You have been connected to the alumni portal.
                  {requestStatus.connectedDate && (
                    <Box sx={{ mt: 1 }}>
                      Connected on: {new Date(requestStatus.connectedDate).toLocaleDateString()}
                    </Box>
                  )}
                </MDTypography>
              )}
              
              {requestStatus.status === 'Rejected' && requestStatus.rejectionReason && (
                <MDTypography variant="body2" color="error" sx={{ mb: 2 }}>
                  <strong>Reason:</strong> {requestStatus.rejectionReason}
                </MDTypography>
              )}
              
              {requestStatus.status === 'Pending' && (
                <MDTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Your request is pending review. You will be notified once it is processed.
                  <Box sx={{ mt: 1 }}>
                    Requested on: {new Date(requestStatus.requestedDate).toLocaleDateString()}
                  </Box>
                </MDTypography>
              )}
              
              <MDButton
                variant="outlined"
                color="primary"
                onClick={() => {
                  setSubmitted(false);
                  setRequestStatus(null);
                  setFormData({
                    erpStudentId: '',
                    studentName: '',
                    email: '',
                    mobile: '',
                    remarks: '',
                  });
                }}
              >
                Submit Another Request
              </MDButton>
            </CardContent>
          </Card>
        </MDBox>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ bgcolor: '#f8f9fa' }}
      >
        <Card sx={{ maxWidth: 600, width: '100%', p: 3 }}>
          <CardContent>
            <MDTypography variant="h4" gutterBottom fontWeight="bold" textAlign="center">
              Request Alumni Portal Access
            </MDTypography>
            <MDTypography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
              Fill in your details to request access to the alumni portal
            </MDTypography>

            <form onSubmit={handleSubmit}>
              <MDBox display="flex" flexDirection="column" gap={2}>
                <TextField
                  name="erpStudentId"
                  label="ERP Student ID *"
                  value={formData.erpStudentId}
                  onChange={handleChange}
                  required
                  fullWidth
                  disabled={loading}
                />
                
                <TextField
                  name="studentName"
                  label="Full Name *"
                  value={formData.studentName}
                  onChange={handleChange}
                  required
                  fullWidth
                  disabled={loading}
                />
                
                <TextField
                  name="email"
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  fullWidth
                  disabled={loading}
                />
                
                <TextField
                  name="mobile"
                  label="Mobile Number"
                  value={formData.mobile}
                  onChange={handleChange}
                  fullWidth
                  disabled={loading}
                />
                
                <TextField
                  name="remarks"
                  label="Remarks (Optional)"
                  multiline
                  rows={3}
                  value={formData.remarks}
                  onChange={handleChange}
                  fullWidth
                  disabled={loading}
                />

                <Box display="flex" gap={2}>
                  <MDButton
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                  >
                    {loading ? 'Submitting...' : 'Submit Request'}
                  </MDButton>
                  
                  <MDButton
                    variant="outlined"
                    color="info"
                    onClick={checkStatus}
                    disabled={loading || !formData.erpStudentId}
                  >
                    Check Status
                  </MDButton>
                </Box>
              </MDBox>
            </form>
          </CardContent>
        </Card>
      </MDBox>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
};

export default StudentPortalRequest;

